"use client";
import { type PropsWithChildren } from "react";
import Anchor from "@/components/common/Anchor";
import Button, { type LinkButton } from "@/components/common/Button";
import TextField from "@/components/site-builder/TextField";
import { ButtonCaptionPosition } from "@/constants/button";
import { TextAlignment } from "@/constants/content";
import { SiteBuilderModuleTextFieldType } from "@/constants/site-builder";
import { ColorVariant, FontVariant } from "@/constants/theme";
import { useGetLinkAttrsByCallToActionConfig } from "@/hooks/useGetLinkAttrsByCallToActionConfig";
import { createThemeColorObject } from "@/utils/theme/createThemeColorObject";
import type { SiteBuilderCallToAction } from "@/types/call-to-action";
import type { SiteBuilderModuleTextField } from "@/types/site-builder";
import s from "./CallToAction.module.scss";

export interface CallToActionProps extends Omit<LinkButton, "href"> {
  config?: SiteBuilderCallToAction;
  as?: "button" | "anchor";
}

export default function CallToAction(
  props: PropsWithChildren<CallToActionProps>,
) {
  const { config, children, as = "button", ...restProps } = props;

  const {
    url,
    label,
    openInNewTab,
    shown,
    size,
    color,
    variant,
    captionText,
    captionPosition,
  } = useGetLinkAttrsByCallToActionConfig(config);

  const renderAsAnchor = as === "anchor";

  if (!shown) {
    return renderAsAnchor ? children : null;
  }

  const ctaLabel = children || label;

  if (renderAsAnchor) {
    return (
      <Anchor href={url} openInNewTab={openInNewTab}>
        {ctaLabel}
      </Anchor>
    );
  }

  const buttonContent = (
    <Button
      href={url}
      openInNewTab={openInNewTab}
      size={size}
      color={color}
      variant={variant}
      {...restProps}
    >
      {ctaLabel}
    </Button>
  );

  if (captionText) {
    const field: SiteBuilderModuleTextField = {
      type: SiteBuilderModuleTextFieldType.Text,
      text: captionText,
      alignment: TextAlignment.Center,
      fontVariant: FontVariant.BodySmall,
      color: createThemeColorObject(ColorVariant.Inherit),
    };
    const captionTextContent = <TextField type="text-small" field={field} />;

    return (
      <div className={s.caption}>
        {captionPosition === ButtonCaptionPosition.Top && captionTextContent}
        {buttonContent}
        {captionPosition === ButtonCaptionPosition.Bottom && captionTextContent}
      </div>
    );
  }

  return buttonContent;
}
